import PageLayout from "@/components/PageLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tag, Percent, TrendingUp, Users, Calendar, Zap, Database, Package, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedFeaturesSection from "@/components/RelatedFeaturesSection";

const Offers = () => {
  useSEO({
    title: 'Dynamic Pricing & Promotions Management | Ommni Offers',
    description: 'Create flexible pricing strategies, promotions, and discounts across all channels. Optimize revenue with dynamic pricing and targeted offers powered by Ommni.',
    keywords: 'dynamic pricing, promotions management, discount management, pricing strategy, offer management',
    canonical: 'https://ommni.io/features/offers',
  });

  const features = [
    {
      icon: Tag,
      title: "Dynamic Pricing",
      description: "Set competitive prices that automatically adjust based on market conditions, inventory, and demand.",
    },
    {
      icon: Percent,
      title: "Flexible Promotions",
      description: "Create sophisticated discount rules, bundles, BOGO offers, and multi-tier promotions.",
    },
    {
      icon: Users,
      title: "Customer Segmentation",
      description: "Target specific customer groups with personalized pricing and exclusive offers.",
    },
    {
      icon: Calendar,
      title: "Scheduled Campaigns",
      description: "Plan and automate promotional campaigns with start/end dates and time-based rules.",
    },
    {
      icon: TrendingUp,
      title: "Performance Analytics",
      description: "Track promotion effectiveness, margin impact, and revenue optimization in real-time.",
    },
    {
      icon: Zap,
      title: "Real-Time Updates",
      description: "Push price and promotion changes to all channels instantly, keeping everything in sync.",
    },
  ];

  const offerTypes = [
    {
      title: "Percentage Discounts",
      description: "Apply percentage-based discounts to products, categories, or entire orders.",
    },
    {
      title: "Fixed Amount Off",
      description: "Reduce prices by a fixed dollar amount on specific products or order totals.",
    },
    {
      title: "Buy One Get One",
      description: "Create BOGO deals, buy X get Y free, and quantity-based promotions.",
    },
    {
      title: "Bundle Pricing",
      description: "Offer discounted pricing when customers purchase product bundles together.",
    },
    {
      title: "Tiered Pricing",
      description: "Volume discounts that reward customers for buying in larger quantities.",
    },
    {
      title: "Flash Sales",
      description: "Time-limited offers that create urgency and drive rapid conversions.",
    },
  ];

  return (
    <PageLayout>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Ommni Offers - Pricing & Promotions Management",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "description": "Maximize revenue with intelligent pricing strategies and powerful promotional tools. Create targeted offers that drive conversions while protecting margins.",
          "offers": {
            "@type": "Offer",
            "price": "500",
            "priceCurrency": "USD",
            "description": "Starting at $500/month"
          },
          "provider": {
            "@type": "Organization",
            "name": "Ommni",
            "url": "https://ommni.io"
          },
          "featureList": [
            "Dynamic Pricing",
            "Flexible Promotions",
            "Customer Segmentation",
            "Scheduled Campaigns",
            "Performance Analytics",
            "Real-Time Updates"
          ]
        })}
      </script>
      <div className="px-4 pb-24">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <Tag className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Pricing & Offers
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Maximize revenue with intelligent pricing strategies and powerful promotional tools. 
              Create targeted offers that drive conversions while protecting margins.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Start Free Trial</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Schedule Demo</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Powerful Pricing Tools</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card key={index} className="p-8 gradient-card">
                  <div className="mb-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Offer Types Section */}
          <div className="mb-20">
            <h2 className="text-3xl font-bold text-center mb-12">Promotion Types</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {offerTypes.map((type, index) => (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="text-lg font-semibold mb-2">{type.title}</h3>
                  <p className="text-sm text-muted-foreground">{type.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Related Features */}
          <RelatedFeaturesSection
            features={[
              {
                icon: Database,
                title: "Product Information Management",
                description: "Manage product pricing alongside complete product data for all channels.",
                link: "/features/pim",
              },
              {
                icon: Package,
                title: "Inventory Management",
                description: "Create inventory-based pricing rules and automatic markdowns.",
                link: "/features/inventory",
              },
              {
                icon: Truck,
                title: "Order Management",
                description: "Apply dynamic pricing at checkout with real-time promotion validation.",
                link: "/features/dom",
              },
            ]}
          />

          {/* CTA Section */}
          <Card className="p-12 bg-primary/5 border-2 border-primary/20 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Drive Revenue with Smart Pricing
            </h2>
            <p className="text-foreground/80 max-w-2xl mx-auto mb-8 text-lg">
              Join leading brands using Ommni to create compelling offers, optimize pricing strategies, 
              and increase profitability across all channels.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="cta" size="lg" asChild>
                <Link to="/free-trial">Get Started</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
};

export default Offers;
